import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  clearAuthDeepLink,
  forgotPassword,
  isValidEmailFormat,
  login,
  normalizeEmail,
  parseAuthDeepLink,
  resetPassword,
  signup,
  verifyOtp,
  writeStoredSession,
} from '../../services/nativeAuth';
import './AuthView.css';

/**
 * Opaque login / signup / OTP / reset gate (xlabs terminal).
 * Not a <form> — HubSpot non-HubSpot form collection ignores this UI.
 * Blueprint: docs/NATIVE_AUTH.md
 */
export default function AuthView({ onAuthenticated, theme = 'blueprint' }) {
  const [mode, setMode] = useState('login'); // login | signup | forgot | otp | reset
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [code, setCode] = useState('');
  const [otpPurpose, setOtpPurpose] = useState('login');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [inviteFlow, setInviteFlow] = useState(false);
  const themeAttr = theme === 'light' ? undefined : theme;

  useEffect(() => {
    if (cooldownSeconds <= 0) return undefined;
    const id = window.setInterval(() => {
      setCooldownSeconds((s) => Math.max(0, s - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [cooldownSeconds]);

  useEffect(() => {
    const deep = parseAuthDeepLink(window.location.search);
    if (!deep) return;
    setInviteFlow(deep.auth === 'invite');
    setEmail(deep.email);
    setCode(deep.code || '');
    setOtpPurpose('reset');
    setMode('reset');
    setInfo(
      deep.auth === 'invite'
        ? 'invite · set a password to continue'
        : 'reset · enter code + new password'
    );
    setError('');
  }, []);

  const locked = cooldownSeconds > 0;
  const cooldownMinutes = Math.ceil(cooldownSeconds / 60);

  const applyTokens = (data) => {
    writeStoredSession({
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      email: data.email,
      expiresAt: data.expiresAt,
    });
    clearAuthDeepLink();
    onAuthenticated?.({
      email: data.email,
      expiresAt: data.expiresAt,
      role: data.role || 'member',
    });
  };

  const handleCredentialSubmit = async () => {
    if (busy || locked) return;
    const normalized = normalizeEmail(email);
    setError('');
    setInfo('');

    if (!isValidEmailFormat(normalized)) {
      setError('enter a valid email address');
      return;
    }
    if (!password || password.length < 10) {
      setError('password must be at least 10 characters');
      return;
    }
    if (mode === 'signup' && password !== password2) {
      setError('passwords do not match');
      return;
    }

    setBusy(true);
    try {
      const result = mode === 'signup'
        ? await signup(normalized, password)
        : await login(normalized, password);

      if (result.status === 429 && result.data.retryAfterSeconds) {
        setCooldownSeconds(result.data.retryAfterSeconds);
        setError(result.data.error || 'wait before retrying');
        return;
      }
      if (!result.ok) {
        setError(result.data.error || 'could not continue');
        return;
      }
      setEmail(normalized);
      setOtpPurpose(result.data.purpose || mode);
      setInfo(`code sent → ${normalized}`);
      setMode('otp');
      setCode('');
      setPassword('');
      setPassword2('');
    } catch {
      setError('could not continue');
    } finally {
      setBusy(false);
    }
  };

  const handleForgot = async () => {
    if (busy || locked) return;
    const normalized = normalizeEmail(email);
    setError('');
    setInfo('');
    if (!isValidEmailFormat(normalized)) {
      setError('enter a valid email address');
      return;
    }
    setBusy(true);
    try {
      const result = await forgotPassword(normalized);
      if (result.status === 429 && result.data.retryAfterSeconds) {
        setCooldownSeconds(result.data.retryAfterSeconds);
        setError(result.data.error || 'wait before retrying');
        return;
      }
      if (!result.ok) {
        setError(result.data.error || 'could not send code');
        return;
      }
      setEmail(normalized);
      if (!result.data.otpRequired) {
        setInfo(result.data.message || 'if that email has an account, check your inbox');
        setError('');
        return;
      }
      setOtpPurpose('reset');
      setInfo(`code sent → ${normalized}`);
      setMode('reset');
      setCode('');
      setPassword('');
      setPassword2('');
    } catch {
      setError('could not send code');
    } finally {
      setBusy(false);
    }
  };

  const handleOtpVerify = async () => {
    if (busy || locked) return;
    setError('');
    setBusy(true);
    try {
      const result = await verifyOtp(email, code, otpPurpose);
      if (result.status === 429 && result.data.retryAfterSeconds) {
        setCooldownSeconds(result.data.retryAfterSeconds);
        setError(result.data.error || 'locked — try again in 10m');
        return;
      }
      if (!result.ok) {
        setError(result.data.error || 'invalid code');
        return;
      }
      applyTokens(result.data);
    } catch {
      setError('could not verify code');
    } finally {
      setBusy(false);
    }
  };

  const handleReset = async () => {
    if (busy || locked) return;
    setError('');
    if (!password || password.length < 10) {
      setError('password must be at least 10 characters');
      return;
    }
    if (password !== password2) {
      setError('passwords do not match');
      return;
    }
    if (code.length !== 6) {
      setError('enter the 6-digit code');
      return;
    }
    setBusy(true);
    try {
      const result = await resetPassword(email, code, password);
      if (result.status === 429 && result.data.retryAfterSeconds) {
        setCooldownSeconds(result.data.retryAfterSeconds);
        setError(result.data.error || 'locked — try again in 10m');
        return;
      }
      if (!result.ok) {
        if (result.data.code === 'ACCESS_REVOKED') {
          setError(result.data.error || 'access revoked — contact your Construx host');
        } else if (inviteFlow) {
          setError(
            result.data.error ||
              'invite code invalid or expired — request a new invite from your Construx contact'
          );
        } else {
          setError(result.data.error || 'could not reset password');
        }
        return;
      }
      applyTokens(result.data);
    } catch {
      setError(
        inviteFlow
          ? 'could not accept invite — try again or request a new invite'
          : 'could not reset password'
      );
    } finally {
      setBusy(false);
    }
  };

  const lead =
    mode === 'signup'
      ? 'create team account'
      : mode === 'forgot'
        ? 'reset password'
        : mode === 'otp'
          ? 'enter the shortcode'
          : mode === 'reset'
            ? inviteFlow
              ? 'accept invite · set password'
              : 'code + new password'
            : 'sign in to continue';

  const promptCmd =
    mode === 'signup'
      ? 'signup'
      : mode === 'forgot'
        ? 'forgot'
        : mode === 'otp'
          ? 'verify'
          : mode === 'reset'
            ? inviteFlow
              ? 'invite'
              : 'reset'
            : 'login';

  return (
    <div
      className="domain-auth"
      data-hub-theme={themeAttr}
      role="dialog"
      aria-modal="true"
      aria-labelledby="native-auth-title"
    >
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter+Tight:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <div className="domain-auth__shell">
        <p className="domain-auth__eyebrow">internal</p>
        <h1 className="domain-auth__brand" id="native-auth-title">
          xlabs <span>/ auth</span>
        </h1>
        <p className="domain-auth__lead">{lead}</p>

        {(mode === 'login' || mode === 'signup') && (
          <div
            className="domain-auth__form"
            data-hs-do-not-collect="true"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleCredentialSubmit();
              }
            }}
          >
            <p className="domain-auth__prompt">
              <span className="k">$</span> {promptCmd}{' '}
              <span className="arg">--email --password</span>
            </p>
            <div className="domain-auth__row">
              <span className="domain-auth__prefix" aria-hidden="true">
                &gt;
              </span>
              <input
                className="domain-auth__input"
                type="text"
                name="auth-email"
                autoComplete="username"
                inputMode="email"
                autoFocus
                placeholder="you@buildconstrux.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError('');
                }}
                disabled={busy || locked}
                aria-label="Work email"
              />
            </div>
            <div className="domain-auth__row">
              <span className="domain-auth__prefix" aria-hidden="true">
                &gt;
              </span>
              <input
                className="domain-auth__input"
                type="password"
                name="auth-password"
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                placeholder="password (≥10)"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError('');
                }}
                disabled={busy || locked}
                aria-label="Password"
              />
            </div>
            {mode === 'signup' ? (
              <div className="domain-auth__row">
                <span className="domain-auth__prefix" aria-hidden="true">
                  &gt;
                </span>
                <input
                  className="domain-auth__input"
                  type="password"
                  name="auth-password2"
                  autoComplete="new-password"
                  placeholder="confirm password"
                  value={password2}
                  onChange={(e) => {
                    setPassword2(e.target.value);
                    if (error) setError('');
                  }}
                  disabled={busy || locked}
                  aria-label="Confirm password"
                />
              </div>
            ) : null}
            <div className="domain-auth__actions">
              <button
                type="button"
                className="domain-auth__submit"
                onClick={handleCredentialSubmit}
                disabled={busy || locked || !email.trim() || !password}
              >
                {busy ? 'sending…' : 'continue'}
              </button>
              {mode === 'login' ? (
                <>
                  <button
                    type="button"
                    className="domain-auth__link"
                    onClick={() => {
                      setMode('signup');
                      setError('');
                      setInfo('');
                    }}
                    disabled={busy}
                  >
                    create account
                  </button>
                  <button
                    type="button"
                    className="domain-auth__link"
                    onClick={() => {
                      setMode('forgot');
                      setError('');
                      setInfo('');
                      setPassword('');
                    }}
                    disabled={busy}
                  >
                    forgot password
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="domain-auth__link"
                  onClick={() => {
                    setMode('login');
                    setError('');
                    setInfo('');
                    setPassword2('');
                  }}
                  disabled={busy}
                >
                  sign in
                </button>
              )}
            </div>
          </div>
        )}

        {mode === 'forgot' && (
          <div
            className="domain-auth__form"
            data-hs-do-not-collect="true"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleForgot();
              }
            }}
          >
            <p className="domain-auth__prompt">
              <span className="k">$</span> forgot <span className="arg">--email</span>
            </p>
            <div className="domain-auth__row">
              <span className="domain-auth__prefix" aria-hidden="true">
                &gt;
              </span>
              <input
                className="domain-auth__input"
                type="text"
                autoComplete="username"
                inputMode="email"
                autoFocus
                placeholder="you@buildconstrux.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError('');
                }}
                disabled={busy || locked}
                aria-label="Work email"
              />
            </div>
            <div className="domain-auth__actions">
              <button
                type="button"
                className="domain-auth__submit"
                onClick={handleForgot}
                disabled={busy || locked || !email.trim()}
              >
                {busy ? 'sending…' : 'send code'}
              </button>
              <button
                type="button"
                className="domain-auth__link"
                onClick={() => {
                  setMode('login');
                  setError('');
                  setInfo('');
                }}
                disabled={busy}
              >
                sign in
              </button>
            </div>
          </div>
        )}

        {mode === 'otp' && (
          <div
            className="domain-auth__form"
            data-hs-do-not-collect="true"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleOtpVerify();
              }
            }}
          >
            <p className="domain-auth__prompt">
              <span className="k">$</span> verify <span className="arg">--code</span>
            </p>
            {info ? <p className="domain-auth__msg domain-auth__msg--ok">{info}</p> : null}
            <div className="domain-auth__row">
              <span className="domain-auth__prefix" aria-hidden="true">
                &gt;
              </span>
              <input
                className="domain-auth__input domain-auth__input--code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                pattern="[0-9]{6}"
                maxLength={6}
                placeholder="······"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                  if (error) setError('');
                }}
                disabled={busy || locked}
                aria-label="6-digit code"
              />
            </div>
            <div className="domain-auth__actions">
              <button
                type="button"
                className="domain-auth__submit"
                onClick={handleOtpVerify}
                disabled={busy || locked || code.length !== 6}
              >
                {busy ? 'checking…' : 'verify'}
              </button>
              <button
                type="button"
                className="domain-auth__link"
                onClick={() => {
                  setMode('login');
                  setCode('');
                  setError('');
                  setInfo('');
                }}
                disabled={busy}
              >
                back
              </button>
            </div>
          </div>
        )}

        {mode === 'reset' && (
          <div
            className="domain-auth__form"
            data-hs-do-not-collect="true"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleReset();
              }
            }}
          >
            <p className="domain-auth__prompt">
              <span className="k">$</span>{' '}
              {inviteFlow ? 'invite' : 'reset'}{' '}
              <span className="arg">--code --password</span>
            </p>
            {email ? (
              <p className="domain-auth__msg domain-auth__msg--ok">{email}</p>
            ) : null}
            {info ? <p className="domain-auth__msg domain-auth__msg--ok">{info}</p> : null}
            <div className="domain-auth__row">
              <span className="domain-auth__prefix" aria-hidden="true">
                &gt;
              </span>
              <input
                className="domain-auth__input domain-auth__input--code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                maxLength={6}
                placeholder="code"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                  if (error) setError('');
                }}
                disabled={busy || locked}
                aria-label="6-digit code"
              />
            </div>
            <div className="domain-auth__row">
              <span className="domain-auth__prefix" aria-hidden="true">
                &gt;
              </span>
              <input
                className="domain-auth__input"
                type="password"
                autoComplete="new-password"
                placeholder="new password (≥10)"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError('');
                }}
                disabled={busy || locked}
                aria-label="New password"
              />
            </div>
            <div className="domain-auth__row">
              <span className="domain-auth__prefix" aria-hidden="true">
                &gt;
              </span>
              <input
                className="domain-auth__input"
                type="password"
                autoComplete="new-password"
                placeholder="confirm password"
                value={password2}
                onChange={(e) => {
                  setPassword2(e.target.value);
                  if (error) setError('');
                }}
                disabled={busy || locked}
                aria-label="Confirm password"
              />
            </div>
            <div className="domain-auth__actions">
              <button
                type="button"
                className="domain-auth__submit"
                onClick={handleReset}
                disabled={busy || locked || code.length !== 6 || !password}
              >
                {busy ? 'saving…' : 'reset'}
              </button>
              <button
                type="button"
                className="domain-auth__link"
                onClick={handleForgot}
                disabled={busy || locked}
              >
                resend
              </button>
              <button
                type="button"
                className="domain-auth__link"
                onClick={() => {
                  setMode('login');
                  setError('');
                  setInfo('');
                }}
                disabled={busy}
              >
                sign in
              </button>
            </div>
          </div>
        )}

        {locked ? (
          <p className="domain-auth__msg domain-auth__msg--error" role="alert">
            locked — retry in {cooldownMinutes}m
          </p>
        ) : error ? (
          <p className="domain-auth__msg domain-auth__msg--error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}
